// 夜空の描画（Roblox の Lighting.TimeOfDay が夜帯のシーン向け）。
//
// 実機の RJF08（TimeOfDay 19:00）・RJF11（00:00）は月と星の出た夜空だが、
// プレビューは固定の明るい昼空だった。ここでは天球の内側に貼る夜空ドーム
// （上が濃紺・地平が藍のグラデーション）と、決定論的に散らした星の点群、
// 空の一角に置く月（柔らかい光の円盤）をまとめて作る。
// すべてカメラに追従させ（背景として無限遠に見せる）、深度を書かない。
import * as THREE from 'three'

/**
 * 天球ドームの半径（studs）。カメラ追従で常に最遠景に置く。
 * カメラの far クリップ面（2000）より内側に収め、ドーム・星・月が
 * クリップされないようにする（star=0.96R≈1536, moon=0.9R≈1440 < 2000）。
 */
const SKY_RADIUS = 1600

/** 星の数。多すぎると点が潰れるので適度に。 */
const STAR_COUNT = 1500

/** 月の見かけの直径（studs、SKY_RADIUS 上での大きさ）。 */
const MOON_DIAMETER = 280

/** 月の方向（単位ベクトル。やや高い空の一角）。 */
const MOON_DIRECTION = new THREE.Vector3(0.45, 0.55, -0.7).normalize()

/** 夜空ドームの天頂色・地平色（濃紺 → 藍）。 */
const SKY_ZENITH = new THREE.Color(0x05081c)
const SKY_HORIZON = new THREE.Color(0x1b2348)

export interface NightSky {
  /** カメラ追従させるグループ（背景として scene に add する）。 */
  readonly group: THREE.Group
  dispose(): void
}

/** 決定論的な疑似乱数（mulberry32）。リロードでも同じ星配置にする。 */
const createRandom = (seed: number): (() => number) => {
  let state = seed >>> 0
  return () => {
    state = (state + 0x6d2b79f5) >>> 0
    let t = state
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** 天頂→地平のグラデーションを内側に貼った夜空ドーム。 */
const createSkyDome = (): THREE.Mesh => {
  // 内側を見るので BackSide。頂点 Y で天頂/地平を補間する。
  const geometry = new THREE.SphereGeometry(SKY_RADIUS, 32, 16)
  const material = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: {
      zenith: { value: new THREE.Color(SKY_ZENITH) },
      horizon: { value: new THREE.Color(SKY_HORIZON) },
    },
    vertexShader: `
      varying float vHeight;
      void main() {
        vHeight = normalize(position).y;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 zenith;
      uniform vec3 horizon;
      varying float vHeight;
      void main() {
        float t = clamp(vHeight * 0.5 + 0.5, 0.0, 1.0);
        // 地平付近をやや厚めに見せるため非線形に持ち上げる。
        gl_FragColor = vec4(mix(horizon, zenith, pow(t, 0.6)), 1.0);
      }
    `,
  })
  const mesh = new THREE.Mesh(geometry, material)
  mesh.renderOrder = -100
  return mesh
}

/** 星の点群（天球面上にランダム配置。地平より下には置かない）。 */
const createStars = (): THREE.Points => {
  const positions = new Float32Array(STAR_COUNT * 3)
  const colors = new Float32Array(STAR_COUNT * 3)
  const random = createRandom(0x9e3779b9)
  const radius = SKY_RADIUS * 0.96
  for (let i = 0; i < STAR_COUNT; i += 1) {
    // 球面一様サンプル（地平 −0.05 より上だけ採用）。
    let y = 0
    let x = 0
    let z = 0
    do {
      const u = random() * 2 - 1
      const phi = random() * Math.PI * 2
      const r = Math.sqrt(1 - u * u)
      x = r * Math.cos(phi)
      y = u
      z = r * Math.sin(phi)
    } while (y < -0.05)
    positions[i * 3] = x * radius
    positions[i * 3 + 1] = y * radius
    positions[i * 3 + 2] = z * radius
    // ほとんど白、わずかに青/黄の個体差。
    const warm = random()
    const c = 0.75 + random() * 0.25
    colors[i * 3] = c
    colors[i * 3 + 1] = c * (warm > 0.5 ? 1 : 0.95)
    colors[i * 3 + 2] = c * (warm > 0.5 ? 0.92 : 1)
  }
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
  // sizeAttenuation=false でスクリーン固定サイズ（px）にする。遠い天球（半径
  // 1536）の点を距離減衰させると 1px 未満に潰れて見えないため、星は常に
  // 2〜3px の点として描く。
  const material = new THREE.PointsMaterial({
    size: 2.5,
    sizeAttenuation: false,
    vertexColors: true,
    transparent: true,
    depthWrite: false,
  })
  const points = new THREE.Points(geometry, material)
  points.renderOrder = -99
  return points
}

/** 月（柔らかい縁の発光円盤）のテクスチャ。 */
const createMoonTexture = (): THREE.CanvasTexture => {
  const size = 128
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext('2d')
  if (context) {
    const center = size / 2
    // 縁を柔らかくフェードさせる放射グラデーション。
    const gradient = context.createRadialGradient(
      center,
      center,
      size * 0.18,
      center,
      center,
      center,
    )
    gradient.addColorStop(0, 'rgba(255,250,235,1)')
    gradient.addColorStop(0.7, 'rgba(245,240,220,0.95)')
    gradient.addColorStop(0.92, 'rgba(220,225,235,0.35)')
    gradient.addColorStop(1, 'rgba(220,225,235,0)')
    context.fillStyle = gradient
    context.fillRect(0, 0, size, size)
    // うっすらクレーター（決定論的）。
    const random = createRandom(0x1234abcd)
    context.globalCompositeOperation = 'source-atop'
    for (let i = 0; i < 14; i += 1) {
      const cx = center + (random() - 0.5) * size * 0.55
      const cy = center + (random() - 0.5) * size * 0.55
      const r = size * (0.03 + random() * 0.06)
      context.fillStyle = `rgba(190,195,205,${0.12 + random() * 0.12})`
      context.beginPath()
      context.arc(cx, cy, r, 0, Math.PI * 2)
      context.fill()
    }
  }
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  return texture
}

/** 月（空の一角に置く発光スプライト）。 */
const createMoon = (texture: THREE.CanvasTexture): THREE.Sprite => {
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    // 月は自発光なので暗い夜空でも明るく見せる（加算ぎみに）。
    blending: THREE.AdditiveBlending,
  })
  const sprite = new THREE.Sprite(material)
  sprite.position.copy(MOON_DIRECTION).multiplyScalar(SKY_RADIUS * 0.9)
  sprite.scale.setScalar(MOON_DIAMETER)
  sprite.renderOrder = -98
  return sprite
}

/**
 * 夜空（ドーム + 星 + 月）を構築する。group はカメラ位置へ毎フレーム追従させる
 * ことで、背景として無限遠に見せる（呼び出し側が camera.position をコピーする）。
 */
export const createNightSky = (): NightSky => {
  const group = new THREE.Group()
  const dome = createSkyDome()
  const stars = createStars()
  const moonTexture = createMoonTexture()
  const moon = createMoon(moonTexture)
  group.add(dome)
  group.add(stars)
  group.add(moon)
  // 背景なので深度・カリングの影響を受けない。
  group.renderOrder = -100
  const disposeMaterial = (
    material: THREE.Material | THREE.Material[],
  ): void => {
    if (Array.isArray(material)) {
      for (const m of material) {
        m.dispose()
      }
    } else {
      material.dispose()
    }
  }
  return {
    group,
    dispose: (): void => {
      dome.geometry.dispose()
      disposeMaterial(dome.material)
      stars.geometry.dispose()
      disposeMaterial(stars.material)
      disposeMaterial(moon.material)
      moonTexture.dispose()
      group.clear()
    },
  }
}

/** 月の向き（ディレクショナルライトを月光に合わせる用）。 */
export const MOON_LIGHT_DIRECTION = MOON_DIRECTION
